import { startCase } from 'lodash';
import {
  Clickable,
  Icon,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Card,
  P,
  Dot,
  Checkbox,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import React, { useMemo } from 'react';
import cn from 'classnames';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import {
  PrintType,
  PurchaseOrder,
  PurchaseOrderVendorMerchType,
} from '../../Lib/Types/purchase-order';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import poItemsMissingDims from '../FnCatalogV2/utils/poItemsMissingDims';
import PoStatus, {
  PurchaseOrderStage,
} from '../../Lib/Constants/purchase-order-status';
import { User } from '../../Lib/Api/vendor';
import { isPoAsnEligible } from '../../Lib/Utils/Asn/isPoAsnEligible';
import { isPrintingApproved } from '../../Lib/Utils/Asn/printingApproved';
import { useCurrentUser } from '../../Lib/Contexts/CurrentUserContext';
import { PurchaseOrderRevisionStatus } from '../../Lib/Types/purchase-order-revision';
import { useNavigateToAsn } from '../../Lib/Hooks/ASN';

type PoCardHeaderButtonsProps = {
  purchaseOrder: PurchaseOrder;
  userId: string;
  handleAcceptModal?: () => void;
  handleRejectModal?: () => void;
  handlePrintUnitLabelModal?: () => void;
  handlePrintHangTagModal?: () => void;
  handleOpenRevisionDrawer?: () => void;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  perms: {
    canAccept: boolean;
    canPrint: boolean;
    canCreateRevision: boolean;
  };
  user: User;
};

const getItemsStatus = (po: PurchaseOrder, printType: PrintType) =>
  po.purchase_order_lines
    .filter((item) => !!item.catalog_item)
    .reduce((status, item) => !!item[printType] && status, true);

export function ActionButtonPopover({
  purchaseOrder,
  userId,
  handleAcceptModal,
  handleRejectModal,
  handlePrintUnitLabelModal,
  handlePrintHangTagModal,
  handleOpenRevisionDrawer,
  perms,
  user,
}: PoCardHeaderButtonsProps) {
  const { flags } = React.useContext(FeatureFlagsContext);

  const isAsnEligible = isPoAsnEligible(
    purchaseOrder,
    !!flags.generate_asn?.enabled,
  );

  const { navigateToAsn, isLoading: isAsnLoading } = useNavigateToAsn(
    userId,
    purchaseOrder.po_number,
    isAsnEligible,
  );

  const dimsMissing = poItemsMissingDims(purchaseOrder);
  const buyerEmail = purchaseOrder.buyer_email;
  const buyerName = ` ${purchaseOrder.buyer_name.split(' ')[0]} ` || ' ';
  const printUnitLabelStatus = getItemsStatus(
    purchaseOrder,
    'dual_sticker_status',
  );
  const printHangtagStatus = getItemsStatus(
    purchaseOrder,
    'hangtag_sticker_status',
  );

  const acceptButtonText = getPoStatus({
    revised_at: purchaseOrder.revised_at,
    vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
    revision_accepted_at: purchaseOrder.revision_accepted_at,
  });

  const acceptDisabled =
    acceptButtonText === PoStatus.ACCEPTED ||
    acceptButtonText === PoStatus.PENDING;

  const isRevisionable =
    flags.po_revision?.enabled &&
    ![PurchaseOrderStage.RECEIVING, PurchaseOrderStage.PAYMENT].includes(
      purchaseOrder.stage || '',
    ) &&
    [
      undefined,
      PurchaseOrderVendorMerchType.DIRECT,
      PurchaseOrderVendorMerchType.THIRD_PARTY,
      PurchaseOrderVendorMerchType.HYBRID,
      PurchaseOrderVendorMerchType.FINISHER,
    ].includes(purchaseOrder.vendor_merch_type || undefined) &&
    [
      undefined,
      PurchaseOrderRevisionStatus.REVISION_PENDING_PD,
      PurchaseOrderRevisionStatus.REVISION_PD_IN_NEGOTIATION,
      PurchaseOrderRevisionStatus.REVISION_REQUESTED,
      PurchaseOrderRevisionStatus.REVISION_NEGOTIATION,
      PurchaseOrderRevisionStatus.REVISION_CANCELLED_BY_NS,
      PurchaseOrderRevisionStatus.REVISION_REJECTED,
      PurchaseOrderRevisionStatus.REVISION_COMPLETED,
    ].includes(purchaseOrder.vendor_revision?.status);

  const { currentUser } = useCurrentUser();
  const isPrintable = useMemo(
    () =>
      isPrintingApproved(user, currentUser) &&
      acceptDisabled &&
      purchaseOrder.is_accepted,
    [user, currentUser, acceptDisabled, purchaseOrder.is_accepted],
  );

  return (
    <Card className="w-60 text-[#1D2433] text-xsm">
      <Card.Body>
        {!acceptDisabled && perms.canAccept && (
          <Clickable
            onClick={handleAcceptModal}
            className={cn(
              'self-stretch px-3 py-2',
              !acceptDisabled && 'hover:bg-neutral-100',
            )}
            disabled={acceptDisabled}
          >
            <Stack direction="row" className="gap-[10px]">
              <Icon
                name="check/check-tick-square-broken"
                className="text-[#1D24338C] text-md"
              />
              <P color={acceptDisabled ? 'disabled' : 'primary'}>
                {startCase(acceptButtonText)}
              </P>
            </Stack>
          </Clickable>
        )}
        {!acceptDisabled && false && (
          <Clickable
            onClick={handleRejectModal}
            className="self-stretch hover:bg-neutral-100 px-3 py-2"
          >
            <Stack direction="row" className="gap-[10px]">
              <Icon
                name="multiple-cross/multiple-cross-cancel-square"
                className="text-[#1D24338C] text-md"
              />
              <P>Reject PO</P>
            </Stack>
          </Clickable>
        )}
        {isPrintable && perms.canPrint && handlePrintUnitLabelModal && (
          <Clickable
            onClick={handlePrintUnitLabelModal}
            className="self-stretch hover:bg-neutral-100 px-3 py-2"
          >
            <Stack direction="row" align="center" className="gap-[10px]">
              <Icon name="bag/paper-bag" className="text-[#1D24338C] text-md" />
              <P>Print All Unit Labels</P>
              <Dot
                className={`ml-auto mr-0 ${printUnitLabelStatus ? 'bg-success-700' : 'bg-neutral-300'}`}
                color="neutral"
              />
            </Stack>
          </Clickable>
        )}
        {isPrintable && perms.canPrint && handlePrintHangTagModal && (
          <Clickable
            onClick={handlePrintHangTagModal}
            className="self-stretch hover:bg-neutral-100 px-3 py-2"
          >
            <Stack direction="row" align="center" className="gap-[10px]">
              <Icon name="tag" className="text-[#1D24338C] text-md" />
              <P>Print All Hangtags</P>
              <Dot
                className={`ml-auto mr-0 ${printHangtagStatus ? 'bg-success-700' : 'bg-neutral-300'}`}
                color="neutral"
              />
            </Stack>
          </Clickable>
        )}
        {false && (
          <Link
            className=" self-stretch hover:bg-neutral-100 px-3 py-2"
            to={`/users/${userId}/purchase-orders/${purchaseOrder.po_number}`}
          >
            <Stack direction="row" align="center" className="gap-[10px]">
              <Icon
                name="pencil-and-scale/scale-pencil-cross"
                className="text-[#1D24338C] text-md"
              />
              <P>Edit Size & Weight</P>
              <Dot
                className="justify-self-end"
                color={dimsMissing === 0 ? 'green' : 'red'}
              />
            </Stack>
          </Link>
        )}
        {!handlePrintUnitLabelModal && (
          <Link
            className=" self-stretch hover:bg-neutral-100 px-3 py-2"
            to={`/users/${userId}/purchase-orders/${purchaseOrder.po_number}`}
          >
            <Stack direction="row" className="gap-[10px]">
              <Icon
                name="maximize/maximize-two-arrow"
                className="text-[#1D24338C] text-md"
              />
              <P>View PO</P>
            </Stack>
          </Link>
        )}
        {isRevisionable && perms.canCreateRevision && (
          <Clickable
            onClick={handleOpenRevisionDrawer}
            className="self-stretch hover:bg-neutral-100 px-3 py-2"
          >
            <Stack direction="row" align="center" className="gap-[10px]">
              <Icon
                name="chat/chat-default"
                className="text-[#1D24338C] text-md"
              />
              {purchaseOrder.is_cancelled ? (
                <P>Request to Reopen</P>
              ) : (
                <P>Request Changes</P>
              )}
            </Stack>
          </Clickable>
        )}
        {isAsnEligible && (
          <Clickable
            onClick={navigateToAsn}
            className={cn('self-stretch hover:bg-neutral-100 px-3 py-2', {
              'pointer-events-none opacity-50': isAsnLoading,
            })}
            disabled={isAsnLoading}
            data-testid="generate-asn-action"
          >
            <Stack direction="row" className="gap-[10px]">
              <Icon
                name="file/file-text"
                className="text-[#1D24338C] text-md"
              />
              <P>Generate ASN</P>
            </Stack>
          </Clickable>
        )}
        {buyerEmail && (
          <a
            className="self-stretch hover:bg-neutral-100 px-3 py-2"
            href={`mailto:${buyerEmail}`}
          >
            <Stack direction="row" className="gap-[10px]">
              <Icon
                name="envelope/envelope-arrow-up"
                className="text-[#1D24338C] text-md"
              />
              <P>{`Email${buyerName}(Buyer)`}</P>
            </Stack>
          </a>
        )}
      </Card.Body>
    </Card>
  );
}

export function PoCardHeaderButtons(props: PoCardHeaderButtonsProps) {
  return (
    <>
      <Stack
        gap="xs"
        direction="row"
        width="none"
        className={cn(
          'absolute h-fit left-2 mt-2',
          !props.selected && 'xl:invisible xl:group-hover:visible',
        )}
      >
        {props.perms.canAccept && (
          <Checkbox checked={props.selected} onChangeValue={props.onSelect} />
        )}
      </Stack>
      <Stack
        gap="xs"
        direction="row"
        width="none"
        className="absolute h-fit right-3 mt-4 xl:invisible xl:group-hover:visible"
      >
        <Popover position="bottom-left" offset={8}>
          {({ isOpen }) => (
            <>
              <Tooltip content="Actions" position="top" arrow>
                <Popover.IconButton
                  size="sm"
                  color="secondary"
                  label="actions"
                  className={isOpen ? 'visible' : 'shadow-none'}
                  icon={<Icon.ThreeDotsMenuHorizontal />}
                />
              </Tooltip>
              <Popover.Content className="group">
                <ActionButtonPopover {...props} />
              </Popover.Content>
              <Tooltip content="Expand" position="top" arrow>
                <Link
                  to={`/users/${props.userId}/purchase-orders/${props.purchaseOrder.po_number}`}
                >
                  <IconButton
                    size="sm"
                    color="secondary"
                    label="expand"
                    className={isOpen ? 'visible' : 'shadow-none'}
                    icon={<Icon.MaximizeTwoArrow />}
                  />
                </Link>
              </Tooltip>
            </>
          )}
        </Popover>
      </Stack>
    </>
  );
}
